import { useEffect, useState } from "react";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, AlertTriangle, RefreshCcw, Undo2 } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function StatPill({ label, value, tone = "slate" }) {
  const cls =
    tone === "amber" ? "bg-amber-900/40 text-amber-200 border-amber-800"
    : tone === "rose" ? "bg-rose-900/40 text-rose-200 border-rose-800"
    : "bg-slate-800/60 text-slate-200 border-slate-700";
  return (
    <span className={`mono text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded ${cls}`}>
      {label}: <b className="font-bold">{value}</b>
    </span>
  );
}

export default function AdminArchivedTrials() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [target, setTarget] = useState(null); // company being reactivated
  const [extendTrial, setExtendTrial] = useState(7);
  const [extendRetention, setExtendRetention] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await platformApi.get("/platform/archived-trials");
      setRows(data.companies || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitReactivate = async () => {
    if (!target) return;
    setBusyId(target.id);
    try {
      await platformApi.post(`/platform/companies/${target.id}/reactivate`, {
        extend_trial_days: Number(extendTrial) || 7,
        extend_data_retention_days: Number(extendRetention) || 30,
      });
      toast.success(`${target.name} reactivated — trial extended ${extendTrial} day(s).`);
      setTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-archived-trials">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Trial-retention archive</div>
          <h1 className="text-3xl font-black mt-1 flex items-center gap-3">
            <Archive className="h-7 w-7 text-rose-400" /> Archived trial companies
          </h1>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            Tenants whose <code className="text-slate-200">trial_data_expires_at</code> elapsed without
            conversion are <b>archived (not deleted)</b> by the daily sweep. Reactivate to restore the
            tenant and grant a fresh trial window.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800"
          data-testid="admin-archived-trials-refresh"
        >
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 mono text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="bg-slate-900 border border-slate-800 rounded-[8px] p-10 text-center"
          data-testid="admin-archived-trials-empty"
        >
          <Archive className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <div className="text-slate-300 font-bold">No archived trial companies</div>
          <div className="text-sm text-slate-500 mt-1">Tenants archived by the data-retention sweep will show up here.</div>
        </div>
      ) : (
        <section className="bg-slate-900 border border-slate-800 rounded-[8px] overflow-hidden" data-testid="admin-archived-trials-table">
          <div className="px-5 py-3 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-400 mono">
            {rows.length} archived trial tenant{rows.length === 1 ? "" : "s"}
          </div>
          <div className="divide-y divide-slate-800">
            {rows.map((c) => (
              <div
                key={c.id}
                className="px-5 py-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                data-testid={`admin-archived-trial-row-${c.id}`}
              >
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-bold text-base text-slate-100">{c.name}</div>
                    <span className="mono text-[10px] uppercase bg-rose-900/40 text-rose-300 border border-rose-800 px-1.5 py-0.5 rounded">Archived</span>
                    {c.is_test_tenant && (
                      <span className="mono text-[10px] uppercase bg-amber-900/40 text-amber-300 border border-amber-800 px-1.5 py-0.5 rounded">Test</span>
                    )}
                    {c.billing_cycle && (
                      <span className="mono text-[10px] uppercase bg-sky-900/40 text-sky-300 border border-sky-800 px-1.5 py-0.5 rounded" data-testid={`admin-archived-cycle-${c.id}`}>
                        {c.billing_cycle}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mono break-all">{c.owner_email}</div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <StatPill label="Bookings" value={c.booking_count} tone={c.booking_count ? "amber" : "slate"} />
                    <StatPill label="Customers" value={c.customer_count} tone={c.customer_count ? "amber" : "slate"} />
                    <StatPill label="Invoices" value={c.invoice_count} />
                    <StatPill label="Managers" value={c.manager_count} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-slate-400 pt-2">
                    <div>Archived at: <span className="text-slate-200 mono">{fmtDate(c.archived_at)}</span></div>
                    <div>Trial data expired: <span className="text-slate-200 mono">{fmtDate(c.trial_data_expires_at)}</span></div>
                    <div>Warning sent: <span className="text-slate-200 mono">{fmtDate(c.trial_retention_warning_sent_at)}</span></div>
                    <div>Created: <span className="text-slate-200 mono">{fmtDate(c.created_at)}</span></div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    onClick={() => { setExtendTrial(7); setExtendRetention(30); setTarget(c); }}
                    disabled={busyId === c.id}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid={`admin-archived-trial-reactivate-${c.id}`}
                  >
                    <Undo2 className="h-4 w-4 mr-2" /> Reactivate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Reactivate confirmation dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-slate-950 border border-slate-800 text-slate-100" data-testid="admin-reactivate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" /> Reactivate {target?.name}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will unarchive the tenant and grant a fresh trial window. All preserved
              data ({target?.booking_count ?? 0} bookings, {target?.customer_count ?? 0} customers)
              becomes accessible to the manager again.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Extend trial (days)</Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={extendTrial}
                onChange={(e) => setExtendTrial(e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                data-testid="admin-reactivate-extend-trial"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Data retention after trial (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={extendRetention}
                onChange={(e) => setExtendRetention(e.target.value)}
                className="bg-slate-900 border-slate-700 text-slate-100"
                data-testid="admin-reactivate-extend-retention"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mono">
            New trial_data_expires_at = now + {(+extendTrial || 0) + (+extendRetention || 0)} days
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} className="border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800" data-testid="admin-reactivate-cancel">
              Cancel
            </Button>
            <Button onClick={submitReactivate} disabled={!!busyId} className="bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="admin-reactivate-confirm">
              {busyId ? "Reactivating…" : "Confirm reactivation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
